import React from 'react';
import { cn } from '@express-labs/raven-tools';
import {
  CarouselProvider,
  ButtonBack,
  ButtonNext,
  Slider,
} from 'pure-react-carousel';
import Thumbnails from '../Thumbnails';
import HorizontalThumbnails from '../HorizontalThumbnails';
import AnimatedSlider from '../AnimatedSlider';
import { SLIDE_HEIGHT, SLIDE_WIDTH } from '../../shared';
import IconLeft from '../IconLeft';
import IconRight from '../IconRight';
import ImageWatcher from '../ImageWatcher';
import s from './CarouselWithThumbs.scss';
import RenderSlide from '../RenderSlide';

type ThumbnailModes = 'left' | 'top' | 'bottom' | 'right';

export type CarouselThumbnailsProps = {
  currentSlide: number;
  deviceWidth?: number;
  mainImages: string[];
  thumbnailMode: ThumbnailModes;
  thumbnailsToShow?: number;
};

const CarouselThumbnails = ({
  deviceWidth,
  mainImages,
  thumbnailMode,
}: CarouselThumbnailsProps) => {
  if (thumbnailMode === 'top' || thumbnailMode === 'bottom') {
    return (
      <HorizontalThumbnails
        deviceWidth={deviceWidth}
        images={mainImages}
        position={thumbnailMode}
        thumbnailsToShow={9}
      />
    );
  } else {
    return (
      <Thumbnails
        deviceWidth={deviceWidth}
        images={mainImages}
        position={thumbnailMode}
      />
    );
  }
};

export type CarouselWithThumbsProps = {
  cards?: never;
  /** Moves the nth slide into the leftmost position of the carousel */
  currentSlide?: number;
  /** The current device's width. */
  deviceWidth: number;
  imageTag?: 'div' | 'img';
  largeImages?: string[];
  /** When true, Pure React Carousel will display a master spinner over the entire carousel until all carousel images have loaded. */
  loading?: boolean;
  /**  array of image url strings */
  mainImages?: string[];
  /** If modal prop is true, clicking on a slide opens a modal window */
  modal?: boolean;
  /** The direction the thumbnails are rendered and scroll. */
  thumbnailMode?: ThumbnailModes;
  visibleSlides?: number;
  zoom?: boolean;
};

export type CarouselWithThumbsPropsInjectedByCarousel = {
  /** Is the Carousel component currently showing a modal window.  Passed in by Carousel. */
  isModalOpen?: boolean;
  /** The index number of an image from the images array currently visible in the modal.  Passed
  in by Carousel. */
  modalImageIndex?: number | null;
  onClick?: (i: number, ev: React.MouseEvent<HTMLElement>) => void;
  onKeyDown?: (i: number, ev: React.KeyboardEvent<HTMLElement>) => void;
  /**  a unique id for the carousel. This number is used to help create unique css id's and class
  names so that you can have more than one carousel on the page and style them differently,
  or reference a dom element via their unique id attribute name via javascript.
  This number is generated automatically in Carousel and passed in. */
  uid: number;
};

function shouldShowSlider(thumbnailMode: ThumbnailModes): boolean {
  return thumbnailMode === 'left' || thumbnailMode === 'right';
}

function getContainerClass(thumbnailMode: ThumbnailModes): string {
  switch (thumbnailMode) {
    case 'top':
      return s.horizontalRow;
    case 'bottom':
      return s.horizontalRowReverse;
    case 'right':
      return s.rowReverse;
    case 'left':
    default:
      return s.row;
  }
}

const CarouselWithThumbs = ({
  currentSlide = 0,
  deviceWidth,
  imageTag = 'div',
  isModalOpen = false,
  largeImages = [],
  loading = false,
  mainImages = [],
  modalImageIndex = null,
  onClick,
  onKeyDown,
  thumbnailMode = 'left',
  uid,
  visibleSlides = 2,
  zoom = true,
}: CarouselWithThumbsProps & CarouselWithThumbsPropsInjectedByCarousel) => {
  return (
    <CarouselProvider
      className={cn(s.container, 'carousel--with-thumbs')}
      currentSlide={currentSlide}
      data-testid="CarouselWithThumbs"
      data-uid={uid}
      hasMasterSpinner={loading}
      lockOnWindowScroll
      naturalSlideHeight={SLIDE_HEIGHT}
      naturalSlideWidth={SLIDE_WIDTH}
      totalSlides={mainImages.length}
      visibleSlides={visibleSlides}
    >
      <div className={getContainerClass(thumbnailMode)}>
        <div className={cn(s.col1, 'carousel--with-thumbs-col1')}>
          <CarouselThumbnails
            currentSlide={currentSlide}
            deviceWidth={deviceWidth}
            mainImages={mainImages}
            thumbnailMode={thumbnailMode}
          />
        </div>
        <div className={cn(s.col2, 'carousel--with-thumbs-col2')}>
          <Slider>
            <ImageWatcher images={mainImages} />
            {mainImages.map((mainImage, i) => (
              <RenderSlide
                i={i}
                imageTag={imageTag}
                isModalOpen={isModalOpen}
                key={mainImage}
                large={largeImages[i]}
                loading={loading}
                main={mainImages[i]}
                modalImageIndex={modalImageIndex}
                onClick={onClick}
                onKeyDown={onKeyDown}
                uid={uid}
                zoom={zoom}
              />
            ))}
          </Slider>
          <ButtonBack className={s.buttonBack}>
            <IconLeft />
          </ButtonBack>
          <ButtonNext className={s.buttonNext}>
            <IconRight />
          </ButtonNext>
        </div>
      </div>
      {shouldShowSlider(thumbnailMode) && <AnimatedSlider threshold={3} />}
    </CarouselProvider>
  );
};

export default CarouselWithThumbs;
